import React from 'react';
import { useParams } from 'react-router-dom';

import { ArchiveBoxIcon, LinkSlashIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { type ColumnDef } from '@oysterjs/ui/data-table';

import {
  CaraWorkspaceOverviewFieldsFragment,
  useArchiveCaraWorkspacesMutation,
  useSearchCaraWorkspacesQuery,
  useUnlinkClientWorkspaceMutation
} from '~/api/graphql';
import { UPDATE_CARA_WORKSPACE, usePermissions } from '~/components/permissions';
import { RowActionsMenu } from '~/components/row-actions-menu';
import {
  CLIENT_TAB_QUERY_OPTIONS,
  ClientListTab,
  useClientConnection
} from '~/components/clients/client-list-tab';
import { ClientTabRoute, eachOf, useClientListControls } from '~/components/clients/client-panels';
import { clientWorkspacesVariables } from '~/api/data/clients/variables';
import { LinkWorkspaceDialog } from '~/components/clients/client-workspace-picker';
import {
  getWorkspaceColumns,
  workspaceSortToCaraWorkspaceSortInput,
  type WorkspaceSortKey
} from '~/components/workspaces/copilot-columns';
import {
  activeFiltersToSearchFilter,
  createFilterDefinitions,
  type WorkspaceFilter
} from '~/api/data/workspaces/copilot-filters';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';

type ClientWorkspace = CaraWorkspaceOverviewFieldsFragment;

const titleOf = (workspace: ClientWorkspace) => workspace.title || 'Untitled workspace';

const actionsColumn = (handlers: {
  onArchive?: (workspaceId: string) => void;
  onUnlink: (workspaceId: string) => void;
}): ColumnDef<ClientWorkspace, WorkspaceSortKey> => ({
  id: 'actions',
  header: '',
  // noHref keeps the row's link off this cell, which would otherwise swallow
  // the click and open the workspace instead of showing the menu.
  noHref: true,
  cellClassName: 'w-0 py-1.5 px-3',
  renderCell: (workspace) => (
    <RowActionsMenu
      label={`Actions for ${titleOf(workspace)}`}
      actions={[
        ...(handlers.onArchive
          ? [
              {
                label: 'Archive workspace',
                icon: ArchiveBoxIcon,
                onSelect: () => handlers.onArchive?.(workspace.id)
              }
            ]
          : []),
        {
          label: 'Unlink from client',
          icon: LinkSlashIcon,
          onSelect: () => handlers.onUnlink(workspace.id)
        }
      ]}
    />
  )
});

const archiveFailed = (count: number) =>
  count === 1
    ? 'Could not archive one workspace. It is still active.'
    : `Could not archive ${count} workspaces. They are still active.`;

const unlinkFailed = (count: number) =>
  count === 1
    ? 'Could not unlink one workspace. It is still on this client.'
    : `Could not unlink ${count} workspaces. They are still on this client.`;

type PendingConfirm = { kind: 'archive' | 'unlink'; ids: string[] };

const ClientWorkspacesTable = ({ clientId }: { clientId: string }) => {
  const filterDefinitions = React.useMemo(() => createFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, sortBy, sortDirection, setSort } = useClientListControls<
    WorkspaceFilter,
    WorkspaceSortKey
  >('date');

  const variables = React.useMemo(
    () =>
      clientWorkspacesVariables(
        clientId,
        activeFiltersToSearchFilter(activeFilters) ?? undefined,
        workspaceSortToCaraWorkspaceSortInput(sortBy, sortDirection)
      ),
    [activeFilters, clientId, sortBy, sortDirection]
  );

  // Archiving or unlinking changes what every workspace search returns, not
  // only this client's, so the whole field goes.
  const query = useSearchCaraWorkspacesQuery({ variables, ...CLIENT_TAB_QUERY_OPTIONS });
  const list = useClientConnection(query, query.data?.searchCaraWorkspaces, variables, {
    fieldName: 'searchCaraWorkspaces'
  });
  const { selected, setSelected, selectedSet, bulk } = list;

  const canArchive = usePermissions().hasAllPermissions(UPDATE_CARA_WORKSPACE);
  const [picking, setPicking] = React.useState(false);
  const [confirm, setConfirm] = React.useState<PendingConfirm | null>(null);
  const [unlinkWorkspace] = useUnlinkClientWorkspaceMutation();
  const [archiveWorkspaces] = useArchiveCaraWorkspacesMutation();

  const runConfirmed = async () => {
    if (!confirm) return;
    const done =
      confirm.kind === 'archive'
        ? await bulk.run(
            'archive',
            async () => [await archiveWorkspaces({ variables: { workspaceIds: confirm.ids } })],
            () => archiveFailed(confirm.ids.length)
          )
        : await bulk.run(
            'unlink',
            () =>
              eachOf(confirm.ids, (workspaceId) =>
                unlinkWorkspace({ variables: { clientId, workspaceId } })
              ),
            unlinkFailed
          );
    setConfirm(null);
    if (done) setSelected([]);
  };

  const columns = React.useMemo(
    () => [
      ...getWorkspaceColumns({
        selectedSet,
        setSelectedWorkspaces: setSelected,
        filtered: list.rows
      }).filter((column) => column.id !== 'client'),
      actionsColumn({
        onArchive: canArchive ? (id) => setConfirm({ kind: 'archive', ids: [id] }) : undefined,
        onUnlink: (id) => setConfirm({ kind: 'unlink', ids: [id] })
      })
    ],
    [canArchive, list.rows, selectedSet, setSelected]
  );

  return (
    <ClientListTab
      list={list}
      noun="workspaces"
      filterDefinitions={filterDefinitions}
      onFiltersChange={setActiveFilters}
      filtered={activeFilters.length > 0}
      placeholder="Filter by status, user, preset, date, or search..."
      action={{ label: 'Link workspace', onClick: () => setPicking(true) }}
      empty={{
        icon: SparklesIcon,
        title: 'No workspaces yet',
        description:
          'Start work for this client, or link an existing workspace, and it will be filed here.'
      }}
      columns={columns}
      rowHref={(workspace) => `/app/workspaces/${workspace.id}`}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={setSort}
    >
      <FloatingSelectionBar
        count={selected.length}
        onClear={() => setSelected([])}
        onArchive={() => setConfirm({ kind: 'archive', ids: selected })}
        archiveEnabled={canArchive}
        isArchiving={bulk.running === 'archive'}
        archiveIcon={<ArchiveBoxIcon className="size-3.5" />}
        extraActions={[
          {
            label: 'Unlink',
            icon: <LinkSlashIcon className="size-3.5" />,
            onClick: () => setConfirm({ kind: 'unlink', ids: selected }),
            loading: bulk.running === 'unlink'
          }
        ]}
      />

      <ArchiveConfirmDialog
        open={confirm !== null}
        count={confirm?.ids.length ?? 0}
        itemNoun="workspace"
        title={
          confirm?.kind === 'unlink'
            ? `Unlink ${confirm.ids.length === 1 ? 'workspace' : 'workspaces'}`
            : undefined
        }
        confirmLabel={confirm?.kind === 'unlink' ? 'Unlink' : undefined}
        description={
          confirm?.kind === 'unlink' ? (
            <>
              Unlink <strong>{confirm.ids.length}</strong>{' '}
              {confirm.ids.length === 1 ? 'workspace' : 'workspaces'} from this client? The
              workspaces themselves are kept, and can be linked again later.
            </>
          ) : (
            <>
              Archive <strong>{confirm?.ids.length ?? 0}</strong>{' '}
              {confirm?.ids.length === 1 ? 'workspace' : 'workspaces'}? You can restore them later
              from the archived workspaces view.
            </>
          )
        }
        loading={bulk.running !== null}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirmed}
      />

      <LinkWorkspaceDialog
        open={picking}
        clientId={clientId}
        onClose={() => setPicking(false)}
        onLinked={list.refresh}
      />
    </ClientListTab>
  );
};

/**
 * A client's workspaces, most recently updated first, a page at a time -- this
 * list runs to the hundreds. The search applies the per-principal visibility
 * filter, so a linked workspace the caller cannot open does not appear.
 */
export const ClientWorkspacesTab = () => {
  const { id = '' } = useParams();
  return (
    <ClientTabRoute noun="workspaces">
      {() => <ClientWorkspacesTable clientId={id} />}
    </ClientTabRoute>
  );
};
